import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LockKey } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import api, { apiError } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPin() {
  const { applyAuth } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async () => {
    if (!identifier.trim()) return toast.error("Enter your phone number or email");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer/forgot", { identifier });
      toast.success(data.message || "If an account exists, a code has been sent.");
      setStep(1);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const resetPin = async () => {
    if (pin !== pin2) return toast.error("PINs do not match");
    if (pin.length !== 4) return toast.error("PIN must be exactly 4 digits");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer/reset", { identifier, otp, new_pin: pin });
      const user = applyAuth(data);
      toast.success("PIN reset — you're signed in");
      nav(homeFor(user.role));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen ok-bg grain relative flex items-center justify-center px-5 py-10">
      <Link to="/login" data-testid="back-login" className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={18} /> Sign in
      </Link>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Logo className="h-14" />
        </Link>
        <div className="card-surface p-7 fade-up">
          <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center mb-4"><LockKey size={22} weight="duotone" className="text-primary" /></div>
          <h1 className="font-display font-bold text-2xl mb-1">Reset your PIN</h1>

          {step === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Enter the phone number or email on your OneKite account. We'll send a one-time code.</p>
              <div>
                <Label className="text-xs text-muted-foreground">Phone or email</Label>
                <Input data-testid="forgot-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="+255… or you@email.com" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <Button data-testid="forgot-send" onClick={sendCode} disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Sending…" : "Send code"}
              </Button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Enter the 6-digit code we sent, then choose a new PIN.</p>
              <div>
                <Label className="text-xs text-muted-foreground">Verification code</Label>
                <Input data-testid="forgot-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl font-mono tracking-widest" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">New PIN</Label>
                <Input data-testid="forgot-pin" type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4-digit PIN" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl font-mono tracking-widest" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Confirm new PIN</Label>
                <Input data-testid="forgot-pin2" type="password" inputMode="numeric" maxLength={4} value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Re-enter 4-digit PIN" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl font-mono tracking-widest" />
              </div>
              <Button data-testid="forgot-reset" onClick={resetPin} disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Resetting…" : "Reset PIN"}
              </Button>
              <button onClick={sendCode} disabled={loading} data-testid="forgot-resend" className="text-sm text-primary font-medium w-full text-center">Resend code</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
